/**
 * The Terraform `config.json` contract.
 *
 * This document is the interface between the control plane and the
 * `qrvey-terraform` installer image: the container curls it and Terraform reads
 * it. The installer is versioned and shipped separately, so **the output shape
 * here is not ours to redesign** — a renamed key is a broken deployment in a
 * customer's account, discovered at apply time.
 *
 * **One image for both clouds, from Qrvey 9.4 onward.** The older
 * `qrvey-terraform-aws` image handled 9.3 and below and is not supported here:
 * every deployment this control plane performs is 9.4+, so there is no reason to
 * carry a second contract.
 *
 * The *input* is a different matter. The prototype accepted every field two
 * ways, `params.variables?.es_config?.size || params.esSize`, because callers
 * were inconsistent about which shape they sent. Nothing depends on that now, so
 * the input is one explicit type and the output is byte-identical to what the
 * prototype produced. Exact output, clean input.
 */
/**
 * The variables Terraform refuses to start without, and their absence is fatal.
 *
 * **This list is observational, not derived**, and that is deliberate rather
 * than lazy. `TerraformVariables` marks a dozen fields non-optional, but the
 * type's required set is not the installer's: `enable_trino`,
 * `staging_instance` and `rabbitmq_service_internal` are declared required here
 * and all carry Terraform defaults, so a run omitting them succeeds. Mirroring
 * the type would refuse runs that would have worked.
 *
 * What is in this list is what a real run was observed to refuse. Run
 * `rmu1f1ycg-7nhk3n` — the first ever started from the console — died with
 * `No value for required variable` naming `registry_key` (variables.tf:144) and
 * `customer_info` (variables.tf:245), having sent neither. `deployment_id` is
 * included because nothing addresses an instance without it.
 *
 * The authoritative answer is the image's own `variables.tf`, and generating
 * this from it is IM-62's job. Until then a name is added here only when a run
 * has actually refused for want of it — a guessed entry blocks a run that would
 * have worked, which is the worse error of the two.
 *
 * **`deployment_id` was here and should never have been** (IM-139). It was
 * added on the reasoning that "nothing addresses an instance without it", which
 * is a fact about this control plane and not about the installer: image
 * `9.5-r-20260831-0793` declares `variable "deployment_id"` with `default = ""`
 * and describes it as *"Deployment ID in case it is a migration"*, and
 * `locals.tf` generates one from `random_string.id` when it is blank, keeps it
 * in Terraform state and recovers it on later runs. A run omitting it succeeds
 * — which is exactly the case this list exists to stop us refusing. Addressing
 * the instance is `newInstanceKey`'s job instead.
 */
export const REQUIRED_RUN_VARIABLES = ['customer_info', 'registry_key'];
/**
 * Which of `REQUIRED_RUN_VARIABLES` a set of variables is missing.
 *
 * Shared so the console can refuse before dispatching and the API can refuse
 * before starting an execution, rather than the two drifting — the same reason
 * `validateRegistration` lives in `shared`. An hour into a container is a poor
 * place to learn that a field was blank.
 *
 * Empty strings count as missing, because the form omits a blank field
 * entirely and a whitespace-only one is not a value either.
 *
 * `customer_info` is checked field by field rather than for mere presence. All
 * four of `CustomerInfo` are required, and a presence check passes the moment
 * any one of them is set — which is not hypothetical: the form defaults Company
 * to the customer id, so a completely untouched form would have looked complete
 * and failed in the container.
 */
const CUSTOMER_INFO_FIELDS = ['firstname', 'lastname', 'email', 'company'];
const isBlank = (value) => value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
export const missingRequiredVariables = (variables, context = {}) => REQUIRED_RUN_VARIABLES.filter((name) => {
    // Supplied by the account, so its absence here is not a missing value.
    if (name === 'registry_key' && context.registryKeyFromAccount === true)
        return false;
    const value = variables[name];
    if (name === 'customer_info') {
        if (value === undefined || value === null || typeof value !== 'object')
            return true;
        const info = value;
        return CUSTOMER_INFO_FIELDS.some((field) => isBlank(info[field]));
    }
    return isBlank(value);
});
/**
 * The longest deployment id the installer's own resource names survive.
 *
 * Observational, exactly like `REQUIRED_RUN_VARIABLES`, and this is the
 * arithmetic rather than a guess. Run `rmu7js63j-o41hs4` — the first plan into
 * a second region — spent four minutes, a container and an image pull in the
 * customer's account to die on two names built from a 22-character id:
 *
 * ```
 * qrvey-{id}-vpc           OpenSearch domain,     max 28  → id ≤ 18
 * qrvey-eks-{id}-cluster-  EKS role name_prefix,  max 38  → id ≤ 19
 * ```
 *
 * 18 is the binding one, so that is the limit. The prefixes belong to the
 * installer image, which ships and versions separately from this repo, so this
 * can only ever be a floor read off a real failure — and it errs the safe way:
 * if the image renames something, a run refused here is a run that would have
 * failed there anyway. The authoritative answer arrives with IM-62, which
 * generates these rules from the image's own `variables.tf`.
 */
export const MAX_DEPLOYMENT_ID_LENGTH = 18;
/**
 * Lowercase letters and digits, because the id's separators are **contradictory
 * between resources** and only the intersection survives all of them.
 *
 * ```
 * qrvey-{id}-vpc   OpenSearch domain  "a-z, 0-9, and - (hyphen)"       no underscore
 * qrvey_{id}       Athena database    "lowercase letters, numbers,     no hyphen
 *                                      or underscore ('_')"
 * ```
 *
 * Both quoted from the provider's own refusal. This rule allowed hyphens for
 * exactly one release, on the OpenSearch message alone — and `amit-0918-west2`
 * then passed it and died on the Athena database instead, in run
 * `rmu7l3tv8-172sgl`, four minutes into the next plan. One resource's charset
 * is not the id's charset.
 *
 * No leading-character rule, though the OpenSearch domain has one: every name
 * observed so far puts `qrvey` in front of the id, and an unevidenced rule
 * refuses runs that would have worked.
 */
const DEPLOYMENT_ID_PATTERN = /^[a-z0-9]+$/;
/**
 * What is wrong with a deployment id, if anything — before a container starts.
 *
 * Absence is deliberately **not** checked here: `missingRequiredVariables` owns
 * that, and two functions answering one question is how they come to disagree.
 * This answers "is this id usable", which is a different question from "was one
 * given" and has a different message.
 *
 * Shared so the console refuses before the request and the API refuses before
 * the execution, with one wording — the arrangement `validateRegistration`
 * already uses.
 */
export const deploymentIdProblem = (value) => {
    if (typeof value !== 'string')
        return undefined;
    const id = value.trim();
    if (id === '')
        return undefined;
    if (id.length > MAX_DEPLOYMENT_ID_LENGTH) {
        return `deployment_id is ${String(id.length)} characters. The installer builds resource names from it — an OpenSearch domain of qrvey-{id}-vpc, which AWS caps at 28 — so it must be ${String(MAX_DEPLOYMENT_ID_LENGTH)} or fewer.`;
    }
    if (!DEPLOYMENT_ID_PATTERN.test(id)) {
        return 'deployment_id may contain only lowercase letters and digits. It becomes part of an OpenSearch domain name, which forbids underscores, and an Athena database name, which forbids hyphens — so neither separator is available.';
    }
    return undefined;
};
/**
 * The oldest chart the single `qrvey-terraform` image supports.
 *
 * Not a default. There deliberately is no default: the value the prototype
 * carried (`9.0.1-20250502`) is a 9.0 chart, which the retired
 * `qrvey-terraform-aws` image handled and the current one does not. A default
 * pointing at an unsupported installer is worse than no default — it would
 * deploy, and fail somewhere deep in an apply.
 *
 * `chartVersion` is therefore required. IM-55 (installer version catalog) is
 * where the list of actually-available versions comes from; until then the caller
 * states it explicitly.
 */
export const MINIMUM_QRVEY_CHART_VERSION = '9.4';
export class InvalidDeploymentConfigError extends Error {
}
/** Five lowercase letters, matching what the prototype generated. */
export const generateDeploymentId = (random = Math.random) => Array.from({ length: 5 }, () => String.fromCharCode(97 + Math.floor(random() * 26))).join('');
/**
 * Rejects any `undefined` anywhere in the document.
 *
 * `JSON.stringify` **drops** undefined-valued keys rather than failing, so a
 * missing input silently produced a config.json with the field absent
 * altogether. Terraform then either substituted its own default or failed deep
 * in an apply, in a customer's account, with nothing pointing back at the
 * cause. Failing here instead is the whole point.
 *
 * The types already forbid it; this catches what they cannot — an
 * `additionalVariables` built from loosely typed data.
 */
export const assertNoUndefined = (value, path = 'config') => {
    if (value === undefined) {
        throw new InvalidDeploymentConfigError(`${path} is undefined, which JSON.stringify would drop`);
    }
    if (Array.isArray(value)) {
        value.forEach((item, index) => assertNoUndefined(item, `${path}[${String(index)}]`));
        return;
    }
    if (typeof value === 'object' && value !== null) {
        for (const [key, item] of Object.entries(value)) {
            assertNoUndefined(item, `${path}.${key}`);
        }
    }
};
/**
 * Where a deployment's state file sits inside the bucket.
 *
 * Preserved from the prototype deliberately: existing deployments' state lives
 * at this key, and changing the convention would strand it.
 *
 * Exported because two other places need the same answer and must not invent
 * their own — the runner wrapper rebuilds it in shell to write `config.json`,
 * and the console names this object when it says where a withheld secret can be
 * read from.
 */
export const stateKeyFor = (deploymentId) => `${deploymentId}/state.tfstate`;
/**
 * Builds the config document. Pure: no S3, no AWS, no clock.
 *
 * Separated from uploading it so the contract can be tested against golden
 * files without a network, which is most of the value of this port.
 */
export const buildTerraformConfig = (input, generateId = generateDeploymentId) => {
    const deploymentId = input.deploymentId ?? generateId();
    if (deploymentId === '') {
        throw new InvalidDeploymentConfigError('deploymentId cannot be empty');
    }
    const config = {
        account_config: {
            access_key_id: input.credentials.accessKeyId,
            secret_access_key: input.credentials.secretAccessKey,
            // Empty string rather than omitted: the installer reads the key
            // unconditionally.
            session_token: input.credentials.sessionToken ?? '',
            region: input.region,
            bucket: input.stateBucket,
            key: input.stateKey ?? stateKeyFor(deploymentId),
            ...(input.lockTable === undefined ? {} : { dynamodb_table: input.lockTable }),
        },
        variables: {
            deployment_id: deploymentId,
            // The prototype read `constants.TERRAFORM_QRVEY_VERSION`, which does not
            // exist, so this shipped as undefined — and therefore absent — unless a
            // caller passed it explicitly. The real key is QRVEY_CHART_VERSION.
            qrvey_chart_version: input.chartVersion,
            registry_user: input.registry.user,
            registry_key: input.registry.key,
            // Whichever backend the caller chose, and only that one. Emitting both
            // would hand the installer contradictory instructions.
            ...(input.es === undefined ? {} : { es_config: input.es }),
            ...(input.opensearch === undefined ? {} : { opensearch_config: input.opensearch }),
            customer_info: input.customer,
            globalization: input.globalization,
            initial_admin_email: input.initialAdminEmail,
            enable_trino: input.features?.enableTrino ?? false,
            staging_instance: input.features?.stagingInstance ?? false,
            rabbitmq_service_internal: input.features?.rabbitmqServiceInternal ?? false,
            openai_api_key: input.openaiApiKey ?? '',
            ...(input.additionalVariables ?? {}),
        },
    };
    assertNoUndefined(config);
    return config;
};
/** The two variable names that select a search backend. */
export const SEARCH_BACKEND_VARIABLES = ['es_config', 'opensearch_config'];
/**
 * What is wrong with a run's search backend, if anything.
 *
 * Checks a caller's arbitrary variables rather than a constructed input, so it
 * cannot lean on the type. Three rules, and the third is the one worth reading:
 *
 * - **Both blocks is a refusal.** `es_config` deploys Elasticsearch inside the
 *   cluster and `opensearch_config` uses the AWS managed service; sending both
 *   asks for two search backends at once, and the installer would resolve that
 *   somehow rather than well.
 * - **A malformed block is a refusal**, naming the field. The alternative is an
 *   apply that fails deep in Terraform with a message about a variable the
 *   caller has to map back to what they typed.
 * - **Neither block is *not* a refusal.** Tempting to require one, and wrong:
 *   whether the installer has its own default is the installer's business, and
 *   inventing a requirement the product does not have would refuse a
 *   configuration that may work. The console always sends one because it offers
 *   the choice; the API does not insist. If it turns out the installer requires
 *   a backend, this is the place to add it — with that fact recorded.
 */
export const searchBackendProblem = (variables) => {
    const present = SEARCH_BACKEND_VARIABLES.filter((name) => variables[name] !== undefined);
    if (present.length > 1) {
        return 'es_config deploys Elasticsearch in the cluster and opensearch_config uses the AWS OpenSearch service; supply one, not both';
    }
    const block = (name) => {
        const value = variables[name];
        return typeof value === 'object' && value !== null && !Array.isArray(value)
            ? value
            : undefined;
    };
    if (variables['es_config'] !== undefined) {
        const es = block('es_config');
        if (es === undefined)
            return 'es_config must be an object';
        if (typeof es['size'] !== 'string' || es['size'] === '') {
            return 'es_config.size must be a chart size name, e.g. "small"';
        }
        if (typeof es['count'] !== 'number')
            return 'es_config.count must be a number';
        if (typeof es['storage'] !== 'string' || es['storage'] === '') {
            return 'es_config.storage must be a Kubernetes storage quantity, e.g. "50Gi"';
        }
    }
    if (variables['opensearch_config'] !== undefined) {
        const os = block('opensearch_config');
        if (os === undefined)
            return 'opensearch_config must be an object';
        if (os['enabled'] !== true) {
            // Supplying the block is the request; disabling it in the same breath is
            // a contradiction that would otherwise surface as a missing domain.
            return 'opensearch_config.enabled must be true — omit the block to not use AWS OpenSearch';
        }
        if (typeof os['instance_type'] !== 'string' || os['instance_type'] === '') {
            return 'opensearch_config.instance_type must be an AWS OpenSearch instance class, e.g. "r6g.large.search"';
        }
        if (typeof os['instance_count'] !== 'number') {
            return 'opensearch_config.instance_count must be a number';
        }
        if (typeof os['volume_size'] !== 'number') {
            return 'opensearch_config.volume_size must be a number of GiB';
        }
    }
    return undefined;
};
/** Longest free-text part of a size worth printing; past this it is not a size name. */
const MAX_SIZE_PART = 40;
const sizePart = (value) => typeof value === 'string' && value !== '' && value.length <= MAX_SIZE_PART ? value : undefined;
const countPart = (value) => typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;
const joined = (parts) => {
    const present = parts.filter((part) => part !== undefined);
    return present.length === 0 ? undefined : present.join(', ');
};
const withSize = (backend, size) => size === undefined ? { backend } : { backend, size };
const INSTALLER_DEFAULT = { backend: 'installer-default' };
/**
 * Which search backend a variables document selects (IM-182).
 *
 * The same two blocks `searchBackendProblem` checks, read for a list rather
 * than refused for a run — so where that function says *what is wrong*, this
 * says *what it is*, and the rule for anything ambiguous is to claim nothing:
 *
 * - **Neither block is `installer-default`**, not "Elasticsearch". Whatever the
 *   installer does without one is the installer's business, for the reason
 *   `searchBackendProblem` records; naming a backend here would be a guess
 *   printed on the one screen people read to find out.
 * - **Both blocks, or a block that is not an object, is also
 *   `installer-default`.** A document the start path would refuse can still be
 *   on a record written before it did, and picking one of two contradictory
 *   blocks is exactly the guess the first rule refuses.
 * - **`opensearch_config` with `enabled: false` selects nothing.** It is how
 *   the block says "not this one", so an `es_config` beside it still selects
 *   Elasticsearch. Any `enabled` other than a boolean is malformed.
 * - **The block decides the backend; its fields decide only the size.** An
 *   object whose `count` is a string still says which backend was asked for,
 *   so the backend is kept and the unreadable part of the size is dropped.
 *
 * Not the console's `searchBackendOf` in `run-document.ts`, which answers which
 * radio button a document being *edited* should show and so has to pick one.
 */
export const searchBackendOf = (variables) => {
    const object = (value) => typeof value === 'object' && value !== null && !Array.isArray(value)
        ? value
        : undefined;
    const esRaw = variables['es_config'];
    const osRaw = variables['opensearch_config'];
    const es = esRaw === undefined ? undefined : object(esRaw);
    const os = osRaw === undefined ? undefined : object(osRaw);
    if ((esRaw !== undefined && es === undefined) || (osRaw !== undefined && os === undefined)) {
        return INSTALLER_DEFAULT;
    }
    const enabled = os?.['enabled'];
    if (os !== undefined && typeof enabled !== 'boolean')
        return INSTALLER_DEFAULT;
    const opensearch = os !== undefined && enabled === true ? os : undefined;
    if (opensearch !== undefined && es !== undefined)
        return INSTALLER_DEFAULT;
    if (opensearch !== undefined) {
        const count = countPart(opensearch['instance_count']);
        const type = sizePart(opensearch['instance_type']);
        const volume = countPart(opensearch['volume_size']);
        return withSize('opensearch', joined([
            count === undefined || type === undefined ? undefined : `${count} × ${type}`,
            volume === undefined ? undefined : `${volume} GiB`,
        ]));
    }
    if (es !== undefined) {
        const count = countPart(es['count']);
        const size = sizePart(es['size']);
        return withSize('elasticsearch', joined([
            count === undefined || size === undefined ? undefined : `${count} × ${size}`,
            // A Kubernetes quantity, printed as written: `50Gi` is not `50 GiB` to
            // someone comparing it with the chart values they typed.
            sizePart(es['storage']),
        ]));
    }
    return INSTALLER_DEFAULT;
};
//# sourceMappingURL=config.js.map